import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box, Card, CardContent, Stack, Typography, Chip, TextField,
  Button, IconButton, Tooltip, CircularProgress, Alert,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer, Paper,
  Autocomplete, ToggleButtonGroup, ToggleButton, InputAdornment, LinearProgress,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  EventAvailable as DailyIcon, Payments as IncomeIcon, Class as ClassIcon,
  CheckCircle as FullIcon, IndeterminateCheckBox as PartialIcon,
  Cancel as NoneIcon, EventBusy as AbsentIcon,
  School as SchoolDayIcon, FreeBreakfast as NoSchoolIcon,
  Save as SaveIcon, Lock as LockIcon, Refresh as RefreshIcon,
} from '@mui/icons-material';
import ModuleHeader from '../../components/ModuleHeader';
import API from '../../BaseURL';
import swal from '../../utils/swal';

const FONT_ACCT = `'IBM Plex Sans', 'Inter', sans-serif`;
const TNUM = { fontVariantNumeric: 'tabular-nums' };

const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const fmtMoney = (n) =>
  Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const STATUS_META = {
  full:    { label: 'Came & paid full',    color: 'success', icon: <FullIcon    fontSize="small" /> },
  partial: { label: 'Came & paid part',    color: 'warning', icon: <PartialIcon fontSize="small" /> },
  none:    { label: "Came & didn't pay",   color: 'error',   icon: <NoneIcon    fontSize="small" /> },
  absent:  { label: 'Did not come',        color: 'default', icon: <AbsentIcon  fontSize="small" /> },
};

export default function DailyCollectionPage() {
  const location = useLocation();
  const [incomeTypes, setIncomeTypes] = useState([]);
  const [incomeType, setIncomeType]   = useState(null);
  const [date]                        = useState(todayISO());
  // Class is asked for before the income type and scopes the roster.
  const [classes, setClasses]         = useState([]);
  const [classFilter, setClassFilter] = useState(null);

  // Auto-select an income type from `?income_type=...` so deep-links from
  // the My Collections cards land directly on the right roster.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const want   = params.get('income_type');
    if (!want || incomeTypes.length === 0) return;
    if (incomeType?.type === want) return;
    const match = incomeTypes.find((t) => t.type === want);
    if (match) setIncomeType({ trans: match.trans, type: match.type, cat: match.cat });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incomeTypes, location.search]);

  // Session state — null when unset, false when "none for today", or object
  const [session, setSession]   = useState(null);
  const [dayMeta, setDayMeta]   = useState(null);   // term + school-week verdict for `date`
  const [hasSubs, setHasSubs]   = useState(false);
  const [roster, setRoster]     = useState([]);
  const [totals, setTotals]     = useState({ expected: 0, received: 0, marked: 0, total: 0 });
  const [loading, setLoading]   = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [closing, setClosing]   = useState(false);

  // Local edit state per row — keeps amount input responsive while typing
  const [rowEdits, setRowEdits] = useState({}); // { student_id: { amount: '' } }

  useEffect(() => {
    (async () => {
      try {
        const [it, c] = await Promise.all([
          API.get('/collections/income-types'),
          API.get('/classes'),
        ]);
        setIncomeTypes(it.data || []);
        setClasses(c.data || []);
      } catch (e) { swal.error(swal.pickError(e, 'Could not load income types')); }
    })();
  }, []);

  // Refresh session + roster when either filter changes. Both are needed:
  // without a class there is nothing to load and nothing is asked for.
  useEffect(() => {
    if (!incomeType || !classFilter) {
      setSession(null); setRoster([]); setRowEdits({}); return;
    }
    loadToday();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incomeType, classFilter]);

  const loadToday = async () => {
    setLoading(true);
    try {
      const r = await API.get('/daily-collections/sessions/today', {
        params: { income_type: incomeType.type, date },
      });
      const sess = r.data?.session || null;
      setSession(sess);
      setHasSubs(!!r.data?.has_subs);
      // Whether the picked date can be worked at all. Comes from the
      // server so this page cannot drift from the rule the save enforces.
      setDayMeta(r.data || null);
      if (sess) await loadRoster(sess.id);
      else { setRoster([]); setTotals({ expected: 0, received: 0, marked: 0, total: 0 }); setRowEdits({}); }
    } catch (e) {
      swal.error(swal.pickError(e, "Could not load today's session"));
    } finally { setLoading(false); }
  };

  const loadRoster = async (sessionId) => {
    try {
      const r = await API.get(`/daily-collections/sessions/${sessionId}`, {
        params: { cla: classFilter || undefined },
      });
      setRoster(r.data?.roster || []);
      setTotals(r.data?.totals || { expected: 0, received: 0, marked: 0, total: 0 });
      // Seed amount edits from already-saved attendance, but only for
      // students we haven't touched in this session.
      setRowEdits(prev => {
        const next = { ...prev };
        for (const row of r.data?.roster || []) {
          if (next[row.student_id] == null) {
            next[row.student_id] = { amount: row.amount_paid != null ? String(row.amount_paid) : '' };
          }
        }
        return next;
      });
    } catch (e) { swal.error(swal.pickError(e, 'Could not load roster')); }
  };

  const incomeOptions = useMemo(
    () => incomeTypes.map(t => ({ trans: t.trans, type: t.type, cat: t.cat })),
    [incomeTypes]
  );
  const classOptions = useMemo(
    () => classes.map(c => c.class).filter(Boolean),
    [classes]
  );

  // ── Open session ───────────────────────────────────────────
  const openYes = async () => {
    try {
      const r = await API.post('/daily-collections/sessions/open', {
        income_type: incomeType.type, session_date: date, is_school_day: 1,
      });
      setSession(r.data?.session || null);
      if (r.data?.session) loadRoster(r.data.session.id);
    } catch (e) { swal.error(swal.pickError(e, 'Could not open session')); }
  };

  const openNo = async () => {
    const ok = await swal.confirm({
      title: 'Mark today as a non-school day?',
      text: 'No charges or arrears will be recorded for any student for today.',
      tone: 'warning',
      confirmText: 'Yes, no school today',
    });
    if (!ok) return;
    try {
      const r = await API.post('/daily-collections/sessions/open', {
        income_type: incomeType.type, session_date: date, is_school_day: 0,
      });
      setSession(r.data?.session || null);
    } catch (e) { swal.error(swal.pickError(e, 'Could not record non-school day')); }
  };

  // ── Record attendance for a row ────────────────────────────
  const recordRow = async (row, status) => {
    const amt = Number(rowEdits[row.student_id]?.amount || row.amount_paid || 0) || 0;
    let amountPaid = amt;
    if (status === 'absent' || status === 'none') amountPaid = 0;
    if (status === 'partial') {
      if (amountPaid <= 0) return swal.error('Enter the amount paid first', 'Missing amount');
      if (amountPaid >= Number(row.daily_amount)) {
        return swal.error('Partial must be less than the daily amount — use Full instead', 'Use Full');
      }
    }
    if (status === 'full') {
      if (amountPaid < Number(row.daily_amount)) amountPaid = Number(row.daily_amount);
    }
    setSavingId(row.student_id);
    try {
      await API.post(`/daily-collections/sessions/${session.id}/attendance`, {
        student_id: row.student_id, status, amount_paid: amountPaid,
      });
      await loadRoster(session.id);
    } catch (e) { swal.error(swal.pickError(e, 'Could not save attendance')); }
    finally { setSavingId(null); }
  };

  // ── Close session ──────────────────────────────────────────
  const closeDay = async () => {
    const left = totals.total - totals.marked;
    if (left > 0) {
      return swal.error(
        `${left} student${left === 1 ? ' is' : 's are'} still unmarked. Mark every student before closing.`,
        'Cannot close yet'
      );
    }
    const ok = await swal.confirm({
      title: "Close today's session?",
      text: 'Once closed, no further changes can be made for today.',
      tone: 'success',
      confirmText: 'Yes, close it',
    });
    if (!ok) return;
    setClosing(true);
    try {
      const r = await API.post(`/daily-collections/sessions/${session.id}/close`);
      setSession(r.data?.session || null);
      swal.success('Session closed');
    } catch (e) { swal.error(swal.pickError(e, 'Could not close session')); }
    finally { setClosing(false); }
  };

  // ── Renders ────────────────────────────────────────────────
  const sessionPill = !session ? null : (
    <Chip size="small"
      icon={session.is_school_day ? <SchoolDayIcon fontSize="small" /> : <NoSchoolIcon fontSize="small" />}
      color={
        session.status === 'closed'    ? 'default' :
        session.status === 'no_school' ? 'warning' :
        'success'
      }
      label={
        session.status === 'closed'    ? 'Closed' :
        session.status === 'no_school' ? 'No school today' :
        'Open'
      }
      sx={{ fontWeight: 700 }} />
  );

  const sessionLocked = session && (session.status === 'closed' || !session.is_school_day);
  // An off day or an out-of-term date. Distinct from sessionLocked: that
  // is a day that WAS opened and is now shut, this is a day that may
  // never be opened in the first place.
  const dayBlocked = dayMeta?.period_open === false;

  return (
    <Box sx={{ maxWidth: 1280, mx: 'auto', fontFamily: FONT_ACCT }}>
      <ModuleHeader
        icon={<DailyIcon />}
        title="Daily Collection"
        subtitle="Mark every registered student's attendance and payment for the day, then close the session"
      />

      {/* Top bar */}
      <Card sx={{ mb: 2, borderRadius: 2 }}>
        <Box sx={{
          px: 2.5, py: 1.4, borderBottom: 1, borderColor: 'divider',
          display: 'flex', alignItems: 'center', gap: 1.5, bgcolor: 'background.paper', flexWrap: 'wrap',
        }}>
          <DailyIcon color="primary" />
          <Typography variant="subtitle2" fontWeight={800}>Today | {date}</Typography>
          <Box sx={{ flex: 1 }} />
          {sessionPill}
          {session && (
            <Tooltip title="Reload roster">
              <IconButton size="small" onClick={() => loadRoster(session.id)}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        <CardContent>
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1.4fr 1fr' },
            gap: 2, alignItems: 'center',
          }}>
            {/* Class FIRST, and required. The roster is fetched for one
                class, never for the whole school — in a school of a few
                thousand the unfiltered roster was the slowest thing on
                the page and it ran before anyone had marked anything. */}
            <Autocomplete size="small" options={classOptions}
              value={classFilter}
              onChange={(_, v) => setClassFilter(v)}
              renderInput={(p) => (
                <TextField {...p} label="Class" required autoFocus
                  helperText="Pick the class first"
                  InputProps={{ ...p.InputProps,
                    startAdornment: (
                      <>
                        <ClassIcon fontSize="small" sx={{ ml: 0.5, mr: 0.5, color: 'text.secondary' }} />
                        {p.InputProps.startAdornment}
                      </>
                    ) }} />
              )} />

            <Autocomplete size="small" options={incomeOptions}
              groupBy={(o) => o.cat}
              value={incomeType}
              onChange={(_, v) => setIncomeType(v)}
              disabled={!classFilter}
              getOptionLabel={(o) => o?.type || ''}
              isOptionEqualToValue={(a, b) => a.trans === b.trans}
              renderInput={(p) => (
                <TextField {...p} label="Income type"
                  helperText={classFilter ? 'Then the collection' : 'Choose a class first'}
                  InputProps={{ ...p.InputProps,
                    startAdornment: (
                      <>
                        <IncomeIcon fontSize="small" sx={{ ml: 0.5, mr: 0.5, color: 'text.secondary' }} />
                        {p.InputProps.startAdornment}
                      </>
                    ) }} />
              )} />

            {/* The date cannot be worked at all — a day the school does
                not collect on, or a term that does not cover it.
                Replaces the school-day gate rather than sitting beside
                it: "Is today a school day?" has only one answer on a day
                that is switched off, and the server would refuse either
                button. */}
            {incomeType && !loading && dayBlocked && (
              <Alert severity="warning" variant="outlined" sx={{ borderRadius: 1.5 }}
                icon={dayMeta?.period_block_kind === 'non_school_day' ? <NoSchoolIcon /> : undefined}>
                <Typography fontWeight={800}>
                  {dayMeta?.period_block_kind === 'non_school_day'
                    ? `No collections on ${dayMeta.day_name ? `a ${dayMeta.day_name}` : 'this day'}`
                    : 'This date cannot be collected on'}
                </Typography>
                <Typography variant="body2">{dayMeta?.period_message}</Typography>
              </Alert>
            )}

            {/* School-day gate */}
            {incomeType && !loading && !dayBlocked && !session && hasSubs && (
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2" fontWeight={700}>Is today a school day?</Typography>
                <Box sx={{ flex: 1 }} />
                <Button size="small" variant="contained" color="success"
                  startIcon={<SchoolDayIcon fontSize="small" />}
                  onClick={openYes}>
                  Yes, open day
                </Button>
                <Button size="small" variant="outlined" color="warning"
                  startIcon={<NoSchoolIcon fontSize="small" />}
                  onClick={openNo}>
                  No school today
                </Button>
              </Stack>
            )}
            {incomeType && !loading && !dayBlocked && !session && !hasSubs && (
              <Alert severity="info" variant="outlined" sx={{ borderRadius: 1.5 }}>
                No students are registered for this income type yet. Use the
                Collection Registration page to add some, then come back.
              </Alert>
            )}
            {!classFilter && (
              <Alert severity="info" variant="outlined" sx={{ borderRadius: 1.5 }}>
                Pick a <strong>class</strong>, then the income type. The register
                loads one class at a time so it stays quick on a large roll.
              </Alert>
            )}
            {loading && (
              <Box sx={{ textAlign: 'right' }}><CircularProgress size={22} /></Box>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Roster */}
      {session && session.is_school_day && (
        <Card sx={{ borderRadius: 2 }}>
          <Box sx={{
            px: 2.5, py: 1.4, borderBottom: 1, borderColor: 'divider',
            display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'background.paper', flexWrap: 'wrap',
          }}>
            <SchoolDayIcon color="primary" />
            <Typography variant="subtitle2" fontWeight={800}>
              Roster - {session.income_type}
              {classFilter ? ` · ${classFilter}` : ''}
            </Typography>
            <Chip size="small" variant="outlined"
              label={`${totals.marked}/${totals.total} marked`}
              color={totals.marked === totals.total && totals.total > 0 ? 'success' : 'default'}
              sx={{ fontWeight: 700 }} />
            <Chip size="small" variant="outlined"
              label={`Expected: GHS ${fmtMoney(totals.expected)}`}
              sx={{ fontWeight: 700 }} />
            <Chip size="small" variant="outlined"
              label={`Received: GHS ${fmtMoney(totals.received)}`}
              color={totals.received > 0 ? 'success' : 'default'}
              sx={{ fontWeight: 700 }} />
            <Box sx={{ flex: 1 }} />
            <Button size="small"
              variant="contained" color="primary"
              startIcon={closing ? <CircularProgress size={14} color="inherit" /> : <LockIcon fontSize="small" />}
              onClick={closeDay}
              disabled={sessionLocked || closing || totals.marked < totals.total || totals.total === 0}>
              {session.status === 'closed' ? 'Closed' : 'Close Day'}
            </Button>
          </Box>

          {totals.total > 0 && (
            <LinearProgress variant="determinate"
              value={totals.total ? (totals.marked / totals.total) * 100 : 0}
              sx={{ height: 4 }} />
          )}

          <CardContent sx={{ pt: 1 }}>
            {roster.length === 0 ? (
              <Box sx={{ py: 6, textAlign: 'center', color: 'text.secondary' }}>
                <DailyIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                <Typography fontWeight={700}>No students registered for this income type</Typography>
              </Box>
            ) : (
              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1.5 }}>
                <Table size="small" sx={{
                  '& .MuiTableCell-head': {
                    bgcolor: 'grey.50', fontSize: 11, fontWeight: 800,
                    textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary',
                  },
                }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Student</TableCell>
                      <TableCell>Class</TableCell>
                      <TableCell align="right" sx={{ width: 100 }}>Daily</TableCell>
                      <TableCell align="right" sx={{ width: 110 }}>Arrears</TableCell>
                      <TableCell align="right" sx={{ width: 110 }}>Credit</TableCell>
                      <TableCell align="right" sx={{ width: 130 }}>Paid (GHS)</TableCell>
                      <TableCell sx={{ width: 340 }}>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {roster.map(r => {
                      const sel = r.status;
                      const rowSaving = savingId === r.student_id;
                      const amtVal = rowEdits[r.student_id]?.amount ?? '';
                      return (
                        <TableRow key={r.student_id}
                          sx={{ '& td': { fontSize: 12, py: 0.7 },
                            bgcolor: sel ? (t) => alpha(t.palette[STATUS_META[sel]?.color === 'default' ? 'grey' : STATUS_META[sel]?.color]?.main || t.palette.grey[500], 0.05) : 'inherit' }}>
                          <TableCell sx={{ fontWeight: 700 }}>{r.student_name || r.student_id}</TableCell>
                          <TableCell>
                            {r.class_name
                              ? <Chip size="small" label={r.class_name} variant="outlined"
                                  sx={{ height: 20, fontWeight: 700 }} />
                              : '-'}
                          </TableCell>
                          <TableCell align="right" sx={TNUM}>GHS {fmtMoney(r.daily_amount)}</TableCell>
                          <TableCell align="right" sx={{
                            ...TNUM, color: Number(r.arrears) > 0 ? 'error.main' : 'text.secondary',
                            fontWeight: Number(r.arrears) > 0 ? 800 : 500 }}>
                            {fmtMoney(r.arrears)}
                          </TableCell>
                          <TableCell align="right" sx={{
                            ...TNUM, color: Number(r.credit) > 0 ? 'success.main' : 'text.secondary',
                            fontWeight: Number(r.credit) > 0 ? 800 : 500 }}>
                            {fmtMoney(r.credit)}
                          </TableCell>
                          <TableCell align="right">
                            <TextField size="small" type="number"
                              value={amtVal}
                              disabled={sessionLocked || rowSaving}
                              onChange={(e) =>
                                setRowEdits(prev => ({ ...prev, [r.student_id]: { amount: e.target.value } }))}
                              placeholder={fmtMoney(r.daily_amount)}
                              inputProps={{ min: 0, step: '0.01',
                                style: { textAlign: 'right', ...TNUM, padding: 4 } }}
                              InputProps={{ startAdornment: <InputAdornment position="start" sx={{ fontSize: 11 }}>GHS</InputAdornment> }}
                              sx={{ width: 130 }} />
                          </TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={0.5} alignItems="center">
                              <ToggleButtonGroup size="small" exclusive
                                value={sel || ''}
                                onChange={(_, v) => { if (v) recordRow(r, v); }}
                                disabled={sessionLocked || rowSaving}>
                                {Object.entries(STATUS_META).map(([k, m]) => (
                                  <Tooltip key={k} title={m.label}>
                                    <ToggleButton value={k}
                                      sx={{ px: 0.6, py: 0.2,
                                        color: (t) => sel === k && m.color !== 'default'
                                          ? t.palette[m.color].main : undefined,
                                        '&.Mui-selected': {
                                          bgcolor: (t) => m.color !== 'default'
                                            ? alpha(t.palette[m.color].main, 0.15) : undefined,
                                          color: (t) => m.color !== 'default'
                                            ? t.palette[m.color].main : undefined,
                                          fontWeight: 800,
                                        },
                                      }}>
                                      {m.icon}
                                    </ToggleButton>
                                  </Tooltip>
                                ))}
                              </ToggleButtonGroup>
                              {rowSaving && <CircularProgress size={14} sx={{ ml: 0.5 }} />}
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}

      {/* No-school banner */}
      {session && !session.is_school_day && (
        <Alert severity="warning" variant="outlined" sx={{ borderRadius: 1.5 }}
          icon={<NoSchoolIcon />}>
          <Typography fontWeight={800}>No school today</Typography>
          <Typography variant="body2">
            This session is closed — no charges or payments will be recorded for {incomeType?.type} on {date}.
          </Typography>
        </Alert>
      )}
    </Box>
  );
}
